/**
 * API client for asynchronous fragment exchange (store-and-forward) and
 * server-side LLM mutation. Entirely optional: if HAS_API is false, the app runs
 * single-node offline and none of this is used.
 *
 * Every call is wrapped so a network failure degrades gracefully — the field must
 * keep running if the venue wifi dies (TECH_PLAN §3).
 */

import { API_URL, HAS_API } from '../config.js';
import type { Fragment, Rhizome } from '@wintermute/orchestrator';

/**
 * Outbound timeouts. A hung request (venue wifi, a cold-starting server) must not
 * stall the field: an abort throws, which the catch below turns into the same
 * `null` as any other failure, so callers take their normal fallback path.
 * Generous for POST because /api/observe waits on a vision model server-side
 * (its own provider timeout is 30s).
 */
const POST_TIMEOUT_MS = 25_000;
const GET_TIMEOUT_MS = 15_000;

async function post<T>(path: string, body: unknown): Promise<T | null> {
  if (!HAS_API) return null;
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(POST_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

async function get<T>(path: string): Promise<T | null> {
  if (!HAS_API) return null;
  try {
    const res = await fetch(`${API_URL}${path}`, { signal: AbortSignal.timeout(GET_TIMEOUT_MS) });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export const api = {
  enabled: HAS_API,

  createSession(sessionId: string, sensingConsent: boolean) {
    return post<{ ok: boolean }>('/api/sessions', { sessionId, sensingConsent });
  },

  /** Push fragments this participant cultivated, for forwarding to others. */
  contribute(sessionId: string, fragments: Fragment[]) {
    return post<{ queued: number }>('/api/fragments/contribute', { sessionId, fragments });
  },

  /** Pull fragments forwarded from other participants (store-and-forward inbox). */
  inbox(sessionId: string, limit = 8) {
    return get<{ fragments: Fragment[] }>(`/api/fragments/inbox?sessionId=${encodeURIComponent(sessionId)}&limit=${limit}`);
  },

  /**
   * Request a server-side LLM rewrite of one fragment under an operator. Returns
   * only the rewritten text; the client builds the child fragment (preserving
   * lineage locally). Falls back to null → client uses the template adapter.
   */
  rewrite(input: {
    op: string;
    text: string;
    lang: string;
    tactic: string;
    dwellContext?: string[];
  }) {
    return post<{ text: string; via: 'llm' | 'template' }>('/api/rewrite', input);
  },

  /** The collective rhizome for the spectator view. */
  rhizome() {
    return get<Rhizome>('/api/rhizome');
  },

  /** Report a SLAM run summary so tracking quality is visible off-device. */
  telemetry(sessionId: string, summary: unknown) {
    return post<{ ok: boolean }>('/api/telemetry', { sessionId, summary });
  },

  /**
   * The Watcher: send a camera frame, receive one in-world observation. Requires
   * the API and the participant's separate, explicit consent. Frames are sent
   * full-resolution to a cloud vision model (see the ethics note in observe.ts).
   */
  observe(input: {
    image: string;
    mediaType?: string;
    dwellContext?: string[];
    sessionId?: string;
    /** Normalised dwell share per tactic — the Watcher's only knowledge of the person. */
    tacticShare?: Record<string, number>;
    elapsedSec?: number;
  }) {
    return post<{
      text: string;
      tactic: string;
      op: string;
      via: 'llm' | 'stub';
      /** One real thing named in the frame, or null. */
      object: string | null;
      /** Its 3x3 grid cell, [col, row]. Fallback when no box was returned. */
      cell: [number, number] | null;
      /** Normalised [x0,y0,x1,y1] bounding box, y down. Preferred over cell. */
      box: [number, number, number, number] | null;
      /** Why it fell back to the stub, when it did. Shown in the drawer. */
      reason?: string;
      /**
       * How the Watcher described its OWN posture in this reply — purely
       * descriptive self-report, not a stage anything advanced through. See
       * REGISTER_VOCABULARY in server/src/observe.ts.
       */
      register?: string;
    }>('/api/observe', input);
  },
};
